'use client';

import { useTranslations } from 'next-intl';
import { Unlink } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import { Select } from './Select';
import {
  buildGitlabAuthConnectUrl,
  buildBitbucketAuthConnectUrl,
  buildGithubAuthConnectUrl,
  buildLinearAppInstallUrl,
  buildLinearAuthConnectUrl,
  createProjectRepository,
  deleteProjectScmConnection,
  deleteProjectRepository,
  fetchBitbucketRepositories,
  fetchGitlabRepositories,
  fetchLinearAppStatus,
  fetchProjectById,
  fetchGithubRepositories,
  fetchLinearTeams,
  fetchProjectMembers,
  fetchProjectRepositories,
  fetchProjectScmConnections,
  fetchTeamMembers,
  type BitbucketRepository,
  type GitlabRepository,
  type GithubRepository,
  type LinearTeam,
  type ProjectMember,
  type ProjectRepository,
  type ProjectScmConnection,
  type ProjectScmProvider,
  type TeamMember,
} from '../lib/api';
import { getAuthSession } from '../lib/authSession';
import { buildGithubOauthPopupRedirectPath, openGithubOauthPopup } from '../lib/githubOAuthPopup';
import { buildGitlabOauthPopupRedirectPath, openGitlabOauthPopup } from '../lib/gitlabOAuthPopup';
import { buildBitbucketOauthPopupRedirectPath, openBitbucketOauthPopup } from '../lib/bitbucketOAuthPopup';
import { buildLinearOauthPopupRedirectPath, openLinearOauthPopup } from '../lib/linearOAuthPopup';
import { canManageTeamMembers, findCurrentTeamMembership } from '../lib/permissions';
import { useSentryConnection } from '../hooks/queries/useSentry';
import { ConnectionStatusChip } from './ConnectionStatusChip';
import DeleteConfirmModal from './DeleteConfirmModal';
import { BitbucketIcon, GitBranchIcon, GitHubIcon, GitLabIcon, LinearIcon } from './icons/ProviderIcons';
import { Spinner } from './loading/Spinner';
import { SentryConnectPanel, SentryConnectedRow } from './ProjectSentryIntegrationView';
import { SectionCard } from './SectionCard';

type ProjectRepositoriesViewProps = {
  projectId: string;
  onRepositoriesChangeAction?: (repositories: ProjectRepository[]) => void;
};

type RepositoryProvider = 'github' | 'gitlab' | 'bitbucket' | 'linear' | 'manual';
// Sentry는 리포지토리 레코드를 만들지 않지만 연결 흐름은 다른 provider와 같으므로 추가 탭에 함께 둔다.
type AddPanelKey = RepositoryProvider | 'sentry';
const addPanelKeys = [
  'github',
  'gitlab',
  'bitbucket',
  'linear',
  'sentry',
  'manual',
] as const satisfies readonly AddPanelKey[];
const defaultGitlabInstanceUrl = 'https://gitlab.com';

const providerKeyMap = {
  GITHUB: 'github',
  GITLAB: 'gitlab',
  BITBUCKET: 'bitbucket',
  LINEAR: 'linear',
  MANUAL: 'manual',
} as const;

// provider는 이름 앞 글리프로만 표시한다(텍스트 배지는 URL 호스트와 정보가 중복됨).
// MANUAL은 특정 벤더가 아니라 중립적인 브랜치 아이콘을 쓴다.
const providerIcon: Record<RepositoryProvider, ReactNode> = {
  github: <GitHubIcon className="h-3.5 w-3.5" />,
  gitlab: <GitLabIcon className="h-3.5 w-3.5" />,
  bitbucket: <BitbucketIcon className="h-3.5 w-3.5" />,
  linear: <LinearIcon className="h-3.5 w-3.5" />,
  manual: <GitBranchIcon className="h-3.5 w-3.5" />,
};

export default function ProjectRepositoriesView({
  projectId,
  onRepositoriesChangeAction,
}: ProjectRepositoriesViewProps) {
  const t = useTranslations('projects.repositories');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const session = useMemo(() => getAuthSession()!, []);

  const [repositories, setRepositories] = useState<ProjectRepository[]>([]);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [name, setName] = useState('');
  const [remoteUrl, setRemoteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingGithubRepo, setIsAddingGithubRepo] = useState(false);
  const [isAddingGitlabRepo, setIsAddingGitlabRepo] = useState(false);
  const [isAddingBitbucketRepo, setIsAddingBitbucketRepo] = useState(false);
  const [isAddingLinearTeam, setIsAddingLinearTeam] = useState(false);
  const [isGithubLoading, setIsGithubLoading] = useState(false);
  const [isGitlabLoading, setIsGitlabLoading] = useState(false);
  const [isBitbucketLoading, setIsBitbucketLoading] = useState(false);
  const [isLinearLoading, setIsLinearLoading] = useState(false);
  const [isGithubConnected, setIsGithubConnected] = useState(false);
  const [isGitlabConnected, setIsGitlabConnected] = useState(false);
  const [isBitbucketConnected, setIsBitbucketConnected] = useState(false);
  const [isLinearConnected, setIsLinearConnected] = useState(false);
  const [isLinearAppInstalled, setIsLinearAppInstalled] = useState(false);
  const [hasLoadedGithubRepositories, setHasLoadedGithubRepositories] = useState(false);
  const [hasLoadedGitlabRepositories, setHasLoadedGitlabRepositories] = useState(false);
  const [hasLoadedBitbucketRepositories, setHasLoadedBitbucketRepositories] = useState(false);
  const [hasLoadedLinearTeams, setHasLoadedLinearTeams] = useState(false);
  const [githubRepositories, setGithubRepositories] = useState<GithubRepository[]>([]);
  const [gitlabRepositories, setGitlabRepositories] = useState<GitlabRepository[]>([]);
  const [bitbucketRepositories, setBitbucketRepositories] = useState<BitbucketRepository[]>([]);
  const [linearTeams, setLinearTeams] = useState<LinearTeam[]>([]);
  const [selectedGithubRepositoryId, setSelectedGithubRepositoryId] = useState('');
  const [selectedGitlabRepositoryId, setSelectedGitlabRepositoryId] = useState('');
  const [selectedBitbucketRepositoryId, setSelectedBitbucketRepositoryId] = useState('');
  const [selectedLinearTeamId, setSelectedLinearTeamId] = useState('');
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [activeAddPanel, setActiveAddPanel] = useState<AddPanelKey>('github');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteRepositoryId, setConfirmDeleteRepositoryId] = useState<string | null>(null);
  const [scmConnections, setScmConnections] = useState<ProjectScmConnection[]>([]);
  const [disconnectingProvider, setDisconnectingProvider] = useState<ProjectScmProvider | null>(null);
  const [confirmDisconnectProvider, setConfirmDisconnectProvider] = useState<ProjectScmProvider | null>(null);

  const currentTeamMembership = useMemo(() => {
    return findCurrentTeamMembership(teamMembers, session.member.id);
  }, [session.member.id, teamMembers]);

  const currentProjectMembership = useMemo(() => {
    return projectMembers.find((member) => member.memberId === session.member.id);
  }, [projectMembers, session.member.id]);

  const canManageByTeamRole = canManageTeamMembers(currentTeamMembership?.role);
  const canManageByProjectRole = canManageTeamMembers(currentProjectMembership?.role);
  const canManage = canManageByTeamRole || canManageByProjectRole;

  // Sentry 연결은 리포지토리와 다른 저장소에 있지만 같은 목록에 행으로 합쳐지므로 여기서 개수를 센다.
  // (행 자체는 SentryConnectedRow가 그린다. React Query가 같은 키를 공유해 요청은 한 번만 나간다.)
  const sentryConnectionQuery = useSentryConnection(session.token, projectId);
  const isSentryConnected = sentryConnectionQuery.data?.isConnected === true;

  const applyRepositories = useCallback(
    (next: ProjectRepository[]) => {
      setRepositories(next);
      onRepositoriesChangeAction?.(next);
    },
    [onRepositoriesChangeAction],
  );

  const reloadScmConnections = useCallback(async () => {
    const connections = await fetchProjectScmConnections(session.token, projectId);
    setScmConnections(connections);
  }, [projectId, session.token]);

  const getProjectConnection = useCallback(
    (provider: ProjectScmProvider) => scmConnections.find((connection) => connection.provider === provider) ?? null,
    [scmConnections],
  );

  const isProjectConnected = useCallback(
    (provider: ProjectScmProvider) => getProjectConnection(provider)?.isConnected === true,
    [getProjectConnection],
  );

  // Linear 팀 키는 연결이 살아 있으면 팀 조회 결과가 정답이고, 끊겼으면 팀 목록이 비어 온다.
  // 그때는 추가 시점에 `{키} - {이름}`으로 저장해 둔 이름에서 되찾는다(이 화면에는 이름 변경이 없다).
  const resolveLinearTeamKey = useCallback(
    (repository: ProjectRepository) => {
      const teamId = repository.metadata?.linearTeamId;
      const team = typeof teamId === 'string' ? linearTeams.find((item) => item.id === teamId) : undefined;
      if (team) return team.key;
      const separatorIndex = repository.name.indexOf(' - ');
      return separatorIndex > 0 ? repository.name.slice(0, separatorIndex) : null;
    },
    [linearTeams],
  );

  const reloadGithubRepositories = useCallback(async () => {
    setIsGithubLoading(true);
    try {
      const githubResult = await fetchGithubRepositories(session.token, projectId);
      setIsGithubConnected(githubResult.connected);
      setGithubRepositories(githubResult.repositories);
      setHasLoadedGithubRepositories(true);
      setSelectedGithubRepositoryId((prev) => {
        if (prev && githubResult.repositories.some((repo) => repo.id.toString() === prev)) {
          return prev;
        }
        return githubResult.repositories[0]?.id.toString() ?? '';
      });
    } catch (error) {
      setHasLoadedGithubRepositories(true);
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsGithubLoading(false);
    }
  }, [projectId, session.token, tRoot]);

  const reloadGitlabRepositories = useCallback(async () => {
    setIsGitlabLoading(true);
    try {
      const gitlabResult = await fetchGitlabRepositories(session.token, projectId);
      setIsGitlabConnected(gitlabResult.connected);
      setGitlabRepositories(gitlabResult.repositories);
      setHasLoadedGitlabRepositories(true);
      setSelectedGitlabRepositoryId((prev) => {
        if (prev && gitlabResult.repositories.some((repo) => repo.id.toString() === prev)) {
          return prev;
        }
        return gitlabResult.repositories[0]?.id.toString() ?? '';
      });
    } catch (error) {
      setHasLoadedGitlabRepositories(true);
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsGitlabLoading(false);
    }
  }, [projectId, session.token, tRoot]);

  const reloadBitbucketRepositories = useCallback(async () => {
    setIsBitbucketLoading(true);
    try {
      const bitbucketResult = await fetchBitbucketRepositories(session.token, projectId);
      setIsBitbucketConnected(bitbucketResult.connected);
      setBitbucketRepositories(bitbucketResult.repositories);
      setHasLoadedBitbucketRepositories(true);
      setSelectedBitbucketRepositoryId((prev) => {
        if (prev && bitbucketResult.repositories.some((repo) => repo.id === prev)) {
          return prev;
        }
        return bitbucketResult.repositories[0]?.id ?? '';
      });
    } catch (error) {
      setHasLoadedBitbucketRepositories(true);
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsBitbucketLoading(false);
    }
  }, [projectId, session.token, tRoot]);

  const reloadLinearTeams = useCallback(async () => {
    setIsLinearLoading(true);
    try {
      const linearResult = await fetchLinearTeams(session.token, projectId);
      setIsLinearConnected(linearResult.connected);
      setLinearTeams(linearResult.teams);
      setHasLoadedLinearTeams(true);
      setSelectedLinearTeamId((prev) => {
        if (prev && linearResult.teams.some((team) => team.id === prev)) {
          return prev;
        }
        return linearResult.teams[0]?.id ?? '';
      });
      if (linearResult.connected) {
        const appStatus = await fetchLinearAppStatus(session.token);
        setIsLinearAppInstalled(appStatus.installed);
      }
    } catch (error) {
      setHasLoadedLinearTeams(true);
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsLinearLoading(false);
    }
  }, [session.token, projectId, tRoot]);

  useEffect(() => {
    if (!projectId) {
      toast.error(t('toastProjectIdMissing'));
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        const project = await fetchProjectById(session.token, projectId);
        const [repositoriesResult, teamMembersResult, projectMembersResult, scmConnectionsResult] = await Promise.all([
          fetchProjectRepositories(session.token, projectId),
          fetchTeamMembers(session.token, project.teamId),
          fetchProjectMembers(session.token, projectId),
          fetchProjectScmConnections(session.token, projectId),
        ]);

        applyRepositories(repositoriesResult);
        setTeamMembers(teamMembersResult);
        setProjectMembers(projectMembersResult);
        setScmConnections(scmConnectionsResult);
      } catch (error) {
        const message = resolveApiErrorToastMessageOrFallback(
          tRoot,
          error,
          tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
        );
        if (message) toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [applyRepositories, projectId, session.token, t, tRoot]);

  useEffect(() => {
    if (!isAddSectionOpen || !canManage) return;

    if (activeAddPanel === 'github' && !hasLoadedGithubRepositories && !isGithubLoading) {
      void reloadGithubRepositories();
      return;
    }

    if (activeAddPanel === 'gitlab' && !hasLoadedGitlabRepositories && !isGitlabLoading) {
      void reloadGitlabRepositories();
      return;
    }

    if (activeAddPanel === 'bitbucket' && !hasLoadedBitbucketRepositories && !isBitbucketLoading) {
      void reloadBitbucketRepositories();
      return;
    }

    if (activeAddPanel === 'linear' && !hasLoadedLinearTeams && !isLinearLoading) {
      void reloadLinearTeams();
    }
  }, [
    activeAddPanel,
    canManage,
    hasLoadedGithubRepositories,
    hasLoadedGitlabRepositories,
    hasLoadedBitbucketRepositories,
    hasLoadedLinearTeams,
    isAddSectionOpen,
    isGithubLoading,
    isGitlabLoading,
    isBitbucketLoading,
    isLinearLoading,
    reloadGithubRepositories,
    reloadGitlabRepositories,
    reloadBitbucketRepositories,
    reloadLinearTeams,
  ]);

  // Linear 연결 상태는 scm-connections에 없고 팀 조회 응답(`connected`)으로만 알 수 있다.
  // 목록에 LINEAR 행이 있을 때만 확인한다 — 조회 API는 프로젝트 read 권한이면 되므로 canManage와 무관하다.
  useEffect(() => {
    if (hasLoadedLinearTeams || isLinearLoading) return;
    if (!repositories.some((repository) => repository.provider === 'LINEAR')) return;
    void reloadLinearTeams();
  }, [hasLoadedLinearTeams, isLinearLoading, reloadLinearTeams, repositories]);

  const handleCreate = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    if (!name.trim()) {
      return;
    }

    const trimmedRemoteUrl = remoteUrl.trim();
    if (trimmedRemoteUrl.length > 0) {
      try {
        const parsed = new URL(trimmedRemoteUrl);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          throw new Error('INVALID_PROTOCOL');
        }
      } catch {
        toast.error(t('toastRemoteUrlInvalid'));
        return;
      }
    }

    setIsCreating(true);
    try {
      const created = await createProjectRepository(session.token, projectId, {
        name: name.trim(),
        provider: 'MANUAL',
        ...(trimmedRemoteUrl.length > 0 ? { remoteUrl: trimmedRemoteUrl } : {}),
      });
      const next = [...repositories, created];
      applyRepositories(next);
      setIsAddSectionOpen(false);
      setName('');
      setRemoteUrl('');
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (repository: ProjectRepository) => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    setConfirmDeleteRepositoryId(repository.id);
  };

  const handleConnectGithub = () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildGithubAuthConnectUrl(
      session.token,
      buildGithubOauthPopupRedirectPath(redirectPath),
      projectId,
    );
    const popup = openGithubOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void Promise.all([reloadScmConnections(), reloadGithubRepositories()]);
      }
    }, 500);
  };

  const handleAddFromGithub = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const selected = githubRepositories.find((repo) => repo.id.toString() === selectedGithubRepositoryId);
    if (!selected) {
      return;
    }

    if (repositories.some((repo) => repo.name === selected.fullName)) {
      toast.error(t('toastGithubAlreadyExists'));
      return;
    }

    setIsAddingGithubRepo(true);
    try {
      const created = await createProjectRepository(session.token, projectId, {
        name: selected.fullName,
        provider: 'GITHUB',
        remoteUrl: selected.cloneUrl,
      });
      const next = [...repositories, created];
      applyRepositories(next);
      setIsAddSectionOpen(false);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsAddingGithubRepo(false);
    }
  };

  const handleConnectGitlab = () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildGitlabAuthConnectUrl(
      session.token,
      buildGitlabOauthPopupRedirectPath(redirectPath),
      defaultGitlabInstanceUrl,
      projectId,
    );
    const popup = openGitlabOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void Promise.all([reloadScmConnections(), reloadGitlabRepositories()]);
      }
    }, 500);
  };

  const handleAddFromGitlab = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const selected = gitlabRepositories.find((repo) => repo.id.toString() === selectedGitlabRepositoryId);
    if (!selected) {
      return;
    }

    if (repositories.some((repo) => repo.name === selected.fullName)) {
      toast.error(t('toastGitlabAlreadyExists'));
      return;
    }

    setIsAddingGitlabRepo(true);
    try {
      const created = await createProjectRepository(session.token, projectId, {
        name: selected.fullName,
        provider: 'GITLAB',
        remoteUrl: selected.cloneUrl,
      });
      const next = [...repositories, created];
      applyRepositories(next);
      setIsAddSectionOpen(false);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsAddingGitlabRepo(false);
    }
  };

  const handleConnectBitbucket = () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildBitbucketAuthConnectUrl(
      session.token,
      buildBitbucketOauthPopupRedirectPath(redirectPath),
      projectId,
    );
    const popup = openBitbucketOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void Promise.all([reloadScmConnections(), reloadBitbucketRepositories()]);
      }
    }, 500);
  };

  const handleAddFromBitbucket = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const selected = bitbucketRepositories.find((repo) => repo.id === selectedBitbucketRepositoryId);
    if (!selected) {
      return;
    }

    if (repositories.some((repo) => repo.name === selected.fullName)) {
      toast.error(t('toastBitbucketAlreadyExists'));
      return;
    }

    setIsAddingBitbucketRepo(true);
    try {
      const created = await createProjectRepository(session.token, projectId, {
        name: selected.fullName,
        provider: 'BITBUCKET',
        remoteUrl: selected.cloneUrl,
      });
      const next = [...repositories, created];
      applyRepositories(next);
      setIsAddSectionOpen(false);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsAddingBitbucketRepo(false);
    }
  };

  const reloadAfterPersonalOauth = async (provider: ProjectScmProvider) => {
    const reloads: Promise<void>[] = [reloadScmConnections()];
    if (canManage) {
      if (provider === 'GITHUB') reloads.push(reloadGithubRepositories());
      if (provider === 'GITLAB') reloads.push(reloadGitlabRepositories());
      if (provider === 'BITBUCKET') reloads.push(reloadBitbucketRepositories());
    }

    const [scmConnectionsResult] = await Promise.allSettled(reloads);
    if (scmConnectionsResult?.status === 'rejected') {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        scmConnectionsResult.reason,
        tRoot('uiErrors.loadFailed', { target: tRoot('uiErrors.targets.repositories') }),
      );
      if (message) toast.error(message);
    }
  };

  // 개인 계정 OAuth는 projectId 없이 시작한다(프로젝트 공유 연결과 분리된 요청자 개인 폴백).
  // 프로젝트 공유 연결(위 handleConnectGithub 등)과 달리 관리 권한 없이도 사용할 수 있다.
  const handleConnectPersonalGithub = () => {
    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildGithubAuthConnectUrl(session.token, buildGithubOauthPopupRedirectPath(redirectPath));
    const popup = openGithubOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void reloadAfterPersonalOauth('GITHUB');
      }
    }, 500);
  };

  const handleConnectPersonalGitlab = () => {
    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildGitlabAuthConnectUrl(
      session.token,
      buildGitlabOauthPopupRedirectPath(redirectPath),
      defaultGitlabInstanceUrl,
    );
    const popup = openGitlabOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void reloadAfterPersonalOauth('GITLAB');
      }
    }, 500);
  };

  const handleConnectPersonalBitbucket = () => {
    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildBitbucketAuthConnectUrl(session.token, buildBitbucketOauthPopupRedirectPath(redirectPath));
    const popup = openBitbucketOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void reloadAfterPersonalOauth('BITBUCKET');
      }
    }, 500);
  };

  const connectPersonalProvider = (provider: ProjectScmProvider) => {
    if (provider === 'GITHUB') handleConnectPersonalGithub();
    if (provider === 'GITLAB') handleConnectPersonalGitlab();
    if (provider === 'BITBUCKET') handleConnectPersonalBitbucket();
  };

  const handleConnectLinear = () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const connectUrl = buildLinearAuthConnectUrl(
      session.token,
      buildLinearOauthPopupRedirectPath(redirectPath),
      projectId,
    );
    const popup = openLinearOauthPopup(connectUrl);
    if (!popup) {
      window.location.assign(connectUrl);
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void reloadLinearTeams();
      }
    }, 500);
  };

  const handleInstallLinearApp = () => {
    const redirectPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';
    const installUrl = buildLinearAppInstallUrl(session.token, buildLinearOauthPopupRedirectPath(redirectPath));
    const popup = openLinearOauthPopup(installUrl);
    if (!popup) {
      window.location.href = installUrl;
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        void reloadLinearTeams();
      }
    }, 500);
  };

  const handleAddFromLinear = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    const selected = linearTeams.find((team) => team.id === selectedLinearTeamId);
    if (!selected) {
      return;
    }

    const repoName = `${selected.key} - ${selected.name}`;

    if (repositories.some((repo) => repo.name === repoName)) {
      toast.error(t('toastLinearAlreadyExists'));
      return;
    }

    setIsAddingLinearTeam(true);
    try {
      const created = await createProjectRepository(session.token, projectId, {
        name: repoName,
        provider: 'LINEAR',
        metadata: { linearTeamId: selected.id },
      });
      const next = [...repositories, created];
      applyRepositories(next);
      setIsAddSectionOpen(false);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setIsAddingLinearTeam(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteRepositoryId) return;
    const target = repositories.find((repository) => repository.id === confirmDeleteRepositoryId);
    if (!target) {
      setConfirmDeleteRepositoryId(null);
      return;
    }

    setDeletingId(confirmDeleteRepositoryId);
    try {
      await deleteProjectRepository(session.token, projectId, confirmDeleteRepositoryId);
      const next = repositories.filter((item) => item.id !== confirmDeleteRepositoryId);
      applyRepositories(next);
      setConfirmDeleteRepositoryId(null);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.deleteFailed', { target: tRoot('uiErrors.targets.repository') }),
      );
      if (message) toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!confirmDisconnectProvider || !canManage) return;
    const provider = confirmDisconnectProvider;
    setDisconnectingProvider(provider);
    try {
      await deleteProjectScmConnection(session.token, projectId, provider);
      await reloadScmConnections();
      if (provider === 'GITHUB') await reloadGithubRepositories();
      if (provider === 'GITLAB') await reloadGitlabRepositories();
      if (provider === 'BITBUCKET') await reloadBitbucketRepositories();
      setConfirmDisconnectProvider(null);
      toast.success(t('disconnectSuccess', { provider: t(`provider.${provider.toLowerCase()}`) }));
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('disconnectFailed'));
      if (message) toast.error(message);
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const confirmDeleteTarget = confirmDeleteRepositoryId
    ? repositories.find((repository) => repository.id === confirmDeleteRepositoryId)
    : null;
  const confirmDeleteName = confirmDeleteTarget?.name ?? '-';
  const connectProjectProvider = (provider: ProjectScmProvider) => {
    if (provider === 'GITHUB') handleConnectGithub();
    if (provider === 'GITLAB') handleConnectGitlab();
    if (provider === 'BITBUCKET') handleConnectBitbucket();
  };
  const connectedItemCount = repositories.length + (isSentryConnected ? 1 : 0);
  // Sentry 연결이 아직 도착하지 않았는데 리포지토리가 비어 있으면 빈 상태가 잠깐 보였다 사라진다.
  // 요청이 실제로 진행 중일 때만 기다린다(비활성 쿼리는 isFetching이 false라 영구 스켈레톤이 되지 않는다).
  const isListLoading = isLoading || (sentryConnectionQuery.isPending && sentryConnectionQuery.isFetching);
  return (
    <div className="space-y-3">
      {!isLoading && !canManage ? (
        <p className="rounded-xl bg-warning/10 px-4 py-3 text-xs text-[var(--chip-warning-fg)]">
          {t('permissionHint')}
        </p>
      ) : null}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-base-content">{t('connectSectionTitle')}</h3>
            {!isAddSectionOpen ? (
              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('addSectionCollapsedDescription')}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm px-0 text-primary hover:bg-transparent"
            disabled={!canManage}
            onClick={() => {
              setActiveAddPanel('github');
              setIsAddSectionOpen((prev) => !prev);
            }}
          >
            {isAddSectionOpen ? t('hideAddSection') : t('showAddSection')}
          </button>
        </div>

        {isAddSectionOpen ? (
          <div className="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-xs">
            <div className="flex flex-wrap gap-2">
              {addPanelKeys.map((panel) => (
                <button
                  key={panel}
                  type="button"
                  className={`btn btn-sm h-9 min-h-9 whitespace-nowrap px-3 leading-none ${activeAddPanel === panel ? 'btn-neutral' : 'btn-ghost bg-base-100'}`}
                  onClick={() => setActiveAddPanel(panel)}
                >
                  {panel === 'github'
                    ? t('githubSectionTitle')
                    : panel === 'gitlab'
                      ? t('gitlabSectionTitle')
                      : panel === 'bitbucket'
                        ? t('bitbucketSectionTitle')
                        : panel === 'linear'
                          ? t('linearSectionTitle')
                          : panel === 'sentry'
                            ? t('sentrySectionTitle')
                            : t('manualSectionTitle')}
                </button>
              ))}
            </div>

            <div className="mt-4">
              {activeAddPanel === 'github' ? (
                <div className="space-y-3">
                  <div className="flex min-h-7 items-center gap-2">
                    <ConnectionStatusChip tone={isGithubConnected ? 'success' : 'neutral'}>
                      {isProjectConnected('GITHUB')
                        ? t('projectCredentialInUse')
                        : isGithubConnected
                          ? t('personalFallbackInUse')
                          : t('notConnected')}
                    </ConnectionStatusChip>
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('githubSectionDescription')}</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                    <div className="form-control">
                      <label
                        className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                        htmlFor="prv-github-repo"
                      >
                        {t('githubRepository')}
                      </label>
                      <Select
                        id="prv-github-repo"
                        fullWidth
                        size="sm"
                        value={selectedGithubRepositoryId}
                        onChange={setSelectedGithubRepositoryId}
                        disabled={!canManage || isGithubLoading || !isGithubConnected}
                        options={
                          isGithubLoading
                            ? [{ value: '', label: t('githubEmpty') }]
                            : githubRepositories.length === 0
                              ? [{ value: '', label: t('githubEmpty') }]
                              : githubRepositories.map((repo) => ({ value: repo.id.toString(), label: repo.fullName }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={!canManage || isGithubLoading}
                      onClick={handleConnectPersonalGithub}
                    >
                      {isGithubConnected ? t('githubReconnect') : t('githubConnect')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={
                        !canManage ||
                        isGithubLoading ||
                        isAddingGithubRepo ||
                        !selectedGithubRepositoryId ||
                        !isGithubConnected
                      }
                      onClick={() => void handleAddFromGithub()}
                    >
                      {isAddingGithubRepo ? t('creating') : t('githubAdd')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeAddPanel === 'gitlab' ? (
                <div className="space-y-3">
                  <div className="flex min-h-7 items-center gap-2">
                    <ConnectionStatusChip tone={isGitlabConnected ? 'success' : 'neutral'}>
                      {isProjectConnected('GITLAB')
                        ? t('projectCredentialInUse')
                        : isGitlabConnected
                          ? t('personalFallbackInUse')
                          : t('notConnected')}
                    </ConnectionStatusChip>
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('gitlabSectionDescription')}</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                    <div className="form-control">
                      <label
                        className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                        htmlFor="prv-gitlab-repo"
                      >
                        {t('gitlabRepository')}
                      </label>
                      <Select
                        id="prv-gitlab-repo"
                        fullWidth
                        size="sm"
                        value={selectedGitlabRepositoryId}
                        onChange={setSelectedGitlabRepositoryId}
                        disabled={!canManage || isGitlabLoading || !isGitlabConnected}
                        options={
                          isGitlabLoading
                            ? [{ value: '', label: t('gitlabEmpty') }]
                            : gitlabRepositories.length === 0
                              ? [{ value: '', label: t('gitlabEmpty') }]
                              : gitlabRepositories.map((repo) => ({ value: repo.id.toString(), label: repo.fullName }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={!canManage || isGitlabLoading}
                      onClick={handleConnectPersonalGitlab}
                    >
                      {isGitlabConnected ? t('gitlabReconnect') : t('gitlabConnect')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={
                        !canManage ||
                        isGitlabLoading ||
                        isAddingGitlabRepo ||
                        !selectedGitlabRepositoryId ||
                        !isGitlabConnected
                      }
                      onClick={() => void handleAddFromGitlab()}
                    >
                      {isAddingGitlabRepo ? t('creating') : t('gitlabAdd')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeAddPanel === 'bitbucket' ? (
                <div className="space-y-3">
                  <div className="flex min-h-7 items-center gap-2">
                    <ConnectionStatusChip tone={isBitbucketConnected ? 'success' : 'neutral'}>
                      {isProjectConnected('BITBUCKET')
                        ? t('projectCredentialInUse')
                        : isBitbucketConnected
                          ? t('personalFallbackInUse')
                          : t('notConnected')}
                    </ConnectionStatusChip>
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('bitbucketSectionDescription')}</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                    <div className="form-control">
                      <label
                        className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                        htmlFor="prv-bitbucket-repo"
                      >
                        {t('bitbucketRepository')}
                      </label>
                      <Select
                        id="prv-bitbucket-repo"
                        fullWidth
                        size="sm"
                        value={selectedBitbucketRepositoryId}
                        onChange={setSelectedBitbucketRepositoryId}
                        disabled={!canManage || isBitbucketLoading || !isBitbucketConnected}
                        options={
                          isBitbucketLoading
                            ? [{ value: '', label: t('bitbucketEmpty') }]
                            : bitbucketRepositories.length === 0
                              ? [{ value: '', label: t('bitbucketEmpty') }]
                              : bitbucketRepositories.map((repo) => ({ value: repo.id, label: repo.fullName }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={!canManage || isBitbucketLoading}
                      onClick={handleConnectPersonalBitbucket}
                    >
                      {isBitbucketConnected ? t('bitbucketReconnect') : t('bitbucketConnect')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={
                        !canManage ||
                        isBitbucketLoading ||
                        isAddingBitbucketRepo ||
                        !selectedBitbucketRepositoryId ||
                        !isBitbucketConnected
                      }
                      onClick={() => void handleAddFromBitbucket()}
                    >
                      {isAddingBitbucketRepo ? t('creating') : t('bitbucketAdd')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeAddPanel === 'linear' ? (
                <div className="space-y-3">
                  <div className="flex min-h-7 items-center gap-2">
                    <ConnectionStatusChip tone={isLinearConnected ? 'success' : 'neutral'}>
                      {isLinearConnected ? t('connected') : t('notConnected')}
                    </ConnectionStatusChip>
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('linearSectionDescription')}</p>
                  </div>
                  {isLinearConnected && !isLinearAppInstalled ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-warning/10 px-4 py-3">
                      <p className="text-xs text-[var(--chip-warning-fg)]">{t('linearAppNotInstalled')}</p>
                      <button
                        type="button"
                        className="btn btn-warning btn-xs whitespace-nowrap"
                        onClick={handleInstallLinearApp}
                      >
                        {t('linearInstallApp')}
                      </button>
                    </div>
                  ) : null}
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                    <div className="form-control">
                      <label
                        className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                        htmlFor="prv-linear-team"
                      >
                        {t('linearTeam')}
                      </label>
                      <Select
                        id="prv-linear-team"
                        fullWidth
                        size="sm"
                        value={selectedLinearTeamId}
                        onChange={setSelectedLinearTeamId}
                        disabled={!canManage || isLinearLoading || !isLinearConnected}
                        options={
                          isLinearLoading
                            ? [{ value: '', label: t('linearEmpty') }]
                            : linearTeams.length === 0
                              ? [{ value: '', label: t('linearEmpty') }]
                              : linearTeams.map((team) => ({ value: team.id, label: `${team.key} - ${team.name}` }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={!canManage || isLinearLoading}
                      onClick={handleConnectLinear}
                    >
                      {isLinearConnected ? t('linearReconnect') : t('linearConnect')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={
                        !canManage ||
                        isLinearLoading ||
                        isAddingLinearTeam ||
                        !selectedLinearTeamId ||
                        !isLinearConnected
                      }
                      onClick={() => void handleAddFromLinear()}
                    >
                      {isAddingLinearTeam ? t('creating') : t('linearAdd')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeAddPanel === 'sentry' ? <SentryConnectPanel projectId={projectId} canManage={canManage} /> : null}

              {activeAddPanel === 'manual' ? (
                <div className="space-y-3">
                  <div className="flex min-h-7 items-center gap-2">
                    <ConnectionStatusChip tone="neutral">{t('provider.manual')}</ConnectionStatusChip>
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('manualSectionDescription')}</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
                    <div className="form-control">
                      <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="prv-name">
                        {t('name')}
                      </label>
                      <input
                        id="prv-name"
                        className="input input-bordered input-sm w-full"
                        placeholder={t('namePlaceholder')}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </div>
                    <div className="form-control">
                      <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="prv-url">
                        {t('remoteUrl')}
                      </label>
                      <input
                        id="prv-url"
                        className="input input-bordered input-sm w-full"
                        placeholder={t('remoteUrlPlaceholder')}
                        value={remoteUrl}
                        onChange={(event) => setRemoteUrl(event.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!canManage || isLoading || isCreating || !name.trim()}
                      onClick={() => void handleCreate()}
                    >
                      {isCreating ? t('creating') : t('create')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <SectionCard
        title={t('connectedSectionTitle')}
        description={t('connectedSectionDescription', { count: String(connectedItemCount) })}
        actions={
          <span className="rounded-full bg-[var(--chip-neutral-bg)] px-2.5 py-1 text-xs text-[var(--chip-neutral-fg)]">
            {t('connectedCount', { count: String(connectedItemCount) })}
          </span>
        }
      >
        {isListLoading ? (
          <div className="space-y-2">
            <div className="skeleton h-14 w-full rounded-xl" />
            <div className="skeleton h-14 w-full rounded-xl" />
          </div>
        ) : connectedItemCount === 0 ? (
          <div className="rounded-xl bg-base-200 p-8 text-center">
            <h3 className="mb-1 font-semibold text-base-content">{t('emptyTitle')}</h3>
            <p className="text-sm text-[var(--color-fg-muted)]">{t('emptyDescription')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-base-200/80">
            {repositories.map((repository) => {
              const provider = providerKeyMap[repository.provider as keyof typeof providerKeyMap] ?? 'manual';
              // 브랜치 조회에 SCM 연결이 필요한 provider만 행에서 연결 상태/액션을 노출한다.
              const scmProvider =
                repository.provider === 'GITHUB' ||
                repository.provider === 'GITLAB' ||
                repository.provider === 'BITBUCKET'
                  ? (repository.provider as ProjectScmProvider)
                  : null;
              const connection = scmProvider ? getProjectConnection(scmProvider) : null;
              const scmConnected = connection?.isConnected === true;
              const personalConnected = connection?.personalConnected === true;
              // Linear는 SCM 연결 축이 아니라 팀 조회 결과로 상태를 판정한다. 조회 전에는 칩을 숨긴다
              // (기본값 "연결 필요"를 먼저 보여 주면 연결된 프로젝트에서 상태가 뒤집혀 보인다).
              const isLinearRow = repository.provider === 'LINEAR';
              const showLinearStatus = isLinearRow && hasLoadedLinearTeams;
              // Linear 팀에는 원격 URL이 없다. 같은 자리에 팀 키를 보여 준다.
              const linearTeamKey = isLinearRow ? resolveLinearTeamKey(repository) : null;
              const secondaryLine = isLinearRow
                ? linearTeamKey && t('linearTeamKey', { key: linearTeamKey })
                : repository.remoteUrl || t('remoteUrlEmpty');
              return (
                <li key={repository.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* 아이콘 자체는 aria-hidden이므로, 접근성 이름은 감싸는 span이 든다. */}
                        <span
                          role="img"
                          aria-label={t(`provider.${provider}`)}
                          title={t(`provider.${provider}`)}
                          className="shrink-0 text-[var(--color-fg-muted)]"
                        >
                          {providerIcon[provider]}
                        </span>
                        <div className="truncate text-sm font-medium">{repository.name}</div>
                        {scmProvider ? (
                          scmConnected ? (
                            <ConnectionStatusChip tone="success">{t('connected')}</ConnectionStatusChip>
                          ) : personalConnected ? (
                            <ConnectionStatusChip tone="success">{t('personalAccountConnected')}</ConnectionStatusChip>
                          ) : (
                            <ConnectionStatusChip tone="warning">{t('connectionNeeded')}</ConnectionStatusChip>
                          )
                        ) : showLinearStatus ? (
                          <ConnectionStatusChip tone={isLinearConnected ? 'success' : 'warning'}>
                            {isLinearConnected ? t('connected') : t('connectionNeeded')}
                          </ConnectionStatusChip>
                        ) : null}
                      </div>
                      {secondaryLine ? (
                        <div className="mt-1 truncate text-xs text-[var(--color-fg-subtle)]">{secondaryLine}</div>
                      ) : null}
                      {scmProvider && scmConnected && connection?.connectedByName ? (
                        <div className="mt-0.5 truncate text-[11px] text-[var(--color-fg-subtle)]">
                          {t('projectConnectionConnectedBy', { name: connection.connectedByName })}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* 미연결 SCM provider: 행의 상시 연결 CTA는 역할/개인 연결 상태에 따라 최대 하나 */}
                      {scmProvider && !scmConnected ? (
                        <>
                          {canManage ? (
                            <button
                              type="button"
                              className="btn btn-outline btn-xs"
                              disabled={disconnectingProvider === scmProvider}
                              onClick={() => connectProjectProvider(scmProvider)}
                            >
                              {t('rowConnectShared')}
                            </button>
                          ) : null}
                          {!canManage && !personalConnected ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => connectPersonalProvider(scmProvider)}
                            >
                              {t('rowConnectPersonal')}
                            </button>
                          ) : null}
                          {canManage || personalConnected ? (
                            <div className="dropdown dropdown-end">
                              <button
                                type="button"
                                tabIndex={0}
                                className="btn btn-ghost btn-xs btn-circle"
                                aria-label={t('personalConnectionOptions')}
                                title={t('personalConnectionOptions')}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <circle cx="12" cy="5" r="1.8" />
                                  <circle cx="12" cy="12" r="1.8" />
                                  <circle cx="12" cy="19" r="1.8" />
                                </svg>
                              </button>
                              <ul
                                tabIndex={0}
                                className="menu dropdown-content z-[1] mt-1 w-40 rounded-box border border-base-300 bg-base-100 p-1 shadow"
                              >
                                <li>
                                  <button type="button" onClick={() => connectPersonalProvider(scmProvider)}>
                                    {t(personalConnected ? 'rowReconnectPersonal' : 'rowConnectPersonal')}
                                  </button>
                                </li>
                              </ul>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                      {/* 연결된 SCM provider: 해제(관리자) */}
                      {scmProvider && scmConnected && canManage ? (
                        <button
                          type="button"
                          aria-label={t('disconnect')}
                          title={t('disconnect')}
                          className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                          disabled={disconnectingProvider === scmProvider}
                          onClick={() => setConfirmDisconnectProvider(scmProvider)}
                        >
                          {disconnectingProvider === scmProvider ? (
                            <Spinner />
                          ) : (
                            <Unlink className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label={tCommon('actions.delete')}
                        title={tCommon('actions.delete')}
                        className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                        disabled={!canManage || deletingId === repository.id}
                        onClick={() => void handleDelete(repository)}
                      >
                        {deletingId === repository.id ? (
                          <Spinner />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
            <SentryConnectedRow projectId={projectId} canManage={canManage} />
          </ul>
        )}
      </SectionCard>
      <DeleteConfirmModal
        isOpen={confirmDisconnectProvider !== null}
        title={t('disconnectTitle')}
        message={t('confirmDisconnect', {
          provider: confirmDisconnectProvider
            ? t(`provider.${confirmDisconnectProvider.toLowerCase() as 'github' | 'gitlab' | 'bitbucket'}`)
            : '-',
        })}
        confirmLabel={t('disconnect')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={disconnectingProvider === confirmDisconnectProvider}
        onConfirm={() => void handleConfirmDisconnect()}
        onCancel={() => {
          if (disconnectingProvider === null) setConfirmDisconnectProvider(null);
        }}
      />
      <DeleteConfirmModal
        isOpen={confirmDeleteRepositoryId !== null}
        title={tCommon('actions.delete')}
        message={t('confirmDelete', { name: confirmDeleteName })}
        confirmLabel={tCommon('actions.delete')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={deletingId === confirmDeleteRepositoryId}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          if (deletingId === null) {
            setConfirmDeleteRepositoryId(null);
          }
        }}
      />
    </div>
  );
}
